import {
  AssetsProvider,
  Image,
  mobile,
  styled,
  Text,
  useMutation,
  useTranslation,
} from '@obvio/app'
import { SvgCloseMenu } from '@obvio/svg'
import {
  Button,
  FormPage,
  Grid,
  GridItem,
  Modal,
  Stack,
  Submit,
  TextArea,
  TextInput,
} from '@obvio/ui'
import { AnimatePresence } from 'framer-motion'
import { useCallback } from 'react'

import { FadeIn } from '../FadeIn'
import { ModalInputs, ModalTitle } from '../Modal'
import { useLocalStorageArt } from '@/utils/hooks/useLocalStorageArt'

import type { Event } from './EventPageVip'
import type { AllowUndefinedForOptional } from '@obvio/utils'
import type { ReactElement } from 'react'

type EventArtModalProps = AllowUndefinedForOptional<{
  open: boolean
  selected?: Event['artworks'][0]
  close(): void
}>

const GridImage = styled(GridItem)`
  position: relative;
`

const ArtTitle = styled.span`
  font-weight: 400;
  font-size: 1rem;
  text-transform: uppercase;
`

const ArtDescription = styled.p`
  font-weight: 300;
  font-size: 1.125rem;
  line-height: 120%;
  white-space: pre-wrap;
  &:nth-child(3) {
    margin-top: 0;
  }
`

const SubmitWrap = styled.div`
  display: flex;
  justify-content: flex-end;
  @media ${mobile} {
    width: 100%;
    > button {
      width: 100%;
    }
  }
`

const GridWrap = styled(Grid)`
  @media ${mobile} {
    grid-template-rows: 11.875rem auto;
    grid-template-columns: 100%;
  }
`

const CenteredButton = styled(Button)`
  margin-left: auto !important;
`

type ReserveFormData = {
  email: string
  name: string
  phone: string
  message: string
}

export function EventArtModal({
  open,
  selected,
  close,
}: EventArtModalProps): ReactElement | null {
  const { t } = useTranslation()
  const { reservedArts, registerArt, removeArt } = useLocalStorageArt()
  const [reserve] = useMutation('reserveProduct')
  const handleSubmit = useCallback(
    ({ email, name, phone, message }: ReserveFormData) => {
      if (selected) {
        void reserve({
          email,
          name,
          phone,
          productId: selected.id,
          message,
        })

        registerArt(selected.id)
      }

      setTimeout(() => {
        close()
      }, 3500)
    },
    [close, reserve, registerArt, selected],
  )

  const reserved = reservedArts?.some((id) => id === selected?.id)

  return (
    <AnimatePresence>
      {open && selected && (
        <AssetsProvider path={`/product/${selected.id}`}>
          <Modal
            CloseIcon={SvgCloseMenu}
            onClose={close}
            onSubmit={handleSubmit}
          >
            <FormPage>
              <FadeIn delay={0} y={25}>
                <Stack kind="vertical">
                  <ModalTitle tag="h2">
                    {reserved
                      ? 'Reserved.'
                      : 'Make a reservation for this artwork.'}
                  </ModalTitle>
                  {reserved ? null : (
                    <ModalInputs kind="vertical">
                      <TextInput name="name" label="FULL NAME" />
                      <TextInput name="email" label="E-MAIL ADDRESS" />
                      <TextInput name="phone" label="PHONE NUMBER" />
                    </ModalInputs>
                  )}
                  <GridWrap templateColumns="17.3125rem 1fr" gap="big">
                    <GridImage noCenter>
                      <Image aspectRatio img={selected.images[0]} />
                    </GridImage>
                    <GridItem noCenter>
                      <Stack kind="vertical" spacing="extraSmall">
                        <ArtTitle>{selected.merchant?.name}</ArtTitle>
                        <ArtDescription>
                          {selected.title}{' '}
                          {selected.year ? `(${selected.year})` : ''}
                        </ArtDescription>
                      </Stack>
                    </GridItem>
                  </GridWrap>
                  {reserved ? (
                    <CenteredButton
                      kind="ghost"
                      onClick={() => removeArt(selected.id)}
                    >
                      {t('event.registerMore')}
                    </CenteredButton>
                  ) : (
                    <>
                      <TextArea
                        kind="ghost"
                        name="message"
                        label="YOUR MESSAGE"
                        placeholder={t('eventArtModel.msgPlaceholder')}
                        rows={3}
                      />
                      <SubmitWrap>
                        <Submit kind="ghost">SEND</Submit>
                      </SubmitWrap>
                    </>
                  )}
                </Stack>
              </FadeIn>
            </FormPage>
            <FormPage successPage>
              <FadeIn>
                <Text tag="h2" as="h4">
                  {t('eventArtModel.thanks')}
                </Text>
              </FadeIn>
            </FormPage>
          </Modal>
        </AssetsProvider>
      )}
    </AnimatePresence>
  )
}
